import { useKeycloakToken } from '../common/useKeycloak';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SourcesList, { sourceSortValues } from './SourcesList';
import { DestinationType, ProviderType, Organization, SourceWithConnections } from '../common/ObjectTypes';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from '../../api/apiUtils';
import loadSources from '../../api/sources/loadSources';
import { loadProviderTypes } from '../../api/connections/loadProviderTypesFilter';
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import { useKeycloak } from '@react-keycloak/web';

const SourcesPage = () => {
    type FilterTypes = {
        provider_type__in: FilterTags[],
        destination_url__in: FilterTags[],
        destination_type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string,
        offset: number
    };
    type FilterTags = {
        name: string,
        id: string
    };
    const defaultSourceFilter: FilterTypes = {
        provider_type__in: [],
        destination_url__in: [],
        destination_type__in: [],
        owner__in: [],
        ordering: "-external_id",
        search: "",
        offset: 0
    };
    const navigate = useNavigate();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak } = useKeycloak();
    const [sources, setSources] = useState<SourceWithConnections[]>([]);
    const [totalSources, setTotalSources] = useState<number>(0);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [loadingProviderTypes, setLoadingProviderTypes] = useState(false);
    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [reloadSources, setReloadSources] = useState(true);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [sourceFilter, setSourceFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('sourceFilter');
        const filter = savedFilter ? { ...defaultSourceFilter, ...JSON.parse(savedFilter) } : defaultSourceFilter;
        // Persisted ordering may predate the Sort By dropdown (old column-header
        // sorts saved e.g. integration__id) — reset it so the dropdown's
        // displayed selection always matches the API ordering.
        if (!sourceSortValues.includes(filter.ordering)) filter.ordering = defaultSourceFilter.ordering;
        return filter;
    });

    const loadSourcesData = async (filter: FilterTypes) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            const [source_results, total_sources, error] = await loadSources(token, "", filter);
            if (!error) {
                const sources_with_connections: SourceWithConnections[] = source_results.map(source => ({
                    source,
                    connections: []
                }));
                setSources(sources_with_connections);
                setTotalSources(total_sources);
                setPageLoaded(true);
            }
            // Always settle the flag — a failed request must not leave the
            // refresh button disabled and spinning forever.
            setReloadSources(false);
        }
    }

    const loadProviderTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingProviderTypes(true);
            try {
                let accumulated: ProviderType[] = [];
                let typesOffset = 0;
                while (true) {
                    const [types, total, error] = await loadProviderTypes(token, typesOffset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (typesOffset + filter_page_size >= total) break;
                    typesOffset += filter_page_size;
                }
                setProviderTypes(accumulated);
            } finally {
                setLoadingProviderTypes(false);
            }
        }
    }

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let urlsOffset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, urlsOffset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (urlsOffset + filter_page_size >= total) break;
                    urlsOffset += filter_page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let typesOffset = 0;
                while (true) {
                    const [types, total, error] = await loadDestinationTypes(token, typesOffset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (typesOffset + filter_page_size >= total) break;
                    typesOffset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    const loadOrganizationsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                let accumulated: Organization[] = [];
                let orgsOffset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(token, orgsOffset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (orgsOffset + filter_page_size >= total) break;
                    orgsOffset += filter_page_size;
                }
                setOrganizations(accumulated);
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    useEffect(() => {
        sessionStorage.setItem('sourceFilter', JSON.stringify(sourceFilter));
        if (reloadSources) loadSourcesData(sourceFilter);
    }, [token, reloadSources, sourceFilter]);

    useEffect(() => {
        if (token) {
            loadDestinationURlsData();
            loadProviderTypesData();
            loadDestinationTypesData();
            loadOrganizationsData();
        }
    }, [token]);

    const handlePageClick = (e: any) => {
        setSourceFilter(prev => ({ ...prev, offset: e.selected * page_size }));
        setReloadSources(true);
    }

    const handleGlobalSearch = (searchSubStr: string) => {
        setSourceFilter(prev => ({ ...prev, search: searchSubStr, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterOwner = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, owner__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterEndpoint = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, destination_url__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterProviderType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, provider_type__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterDestinationType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, destination_type__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleSortBy = (selected: { label: string, value: string }) => {
        setSourceFilter(prev => ({ ...prev, ordering: selected.value, offset: 0 }));
        setReloadSources(true);
    }

    const handleToggleDestinationDetailsModal = async (dest_id: string) => {
        if (dest_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            navigate(`/sources/destination/${dest_id}/configuration`, { replace: false });
        }
    }

    const handleViewConnectionDetails = (connection_id: string) => {
        navigate(`/connections/${connection_id}`, { replace: false });
    }

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const endpointSelectValue = toSingleValue(sourceFilter.destination_url__in);
    const ownerSelectValue = toSingleValue(sourceFilter.owner__in);
    const providerTypeSelectValue = toSingleValue(sourceFilter.provider_type__in);
    const destinationTypeSelectValue = toSingleValue(sourceFilter.destination_type__in);

    return (
            <div>
                <SourcesList
                    sources={sources}
                    totalCount={totalSources}
                    take={page_size}
                    offset={sourceFilter.offset}
                    onPageClicked={handlePageClick}
                    loading={reloadSources}
                    organizations={organizations}
                    loadingOrganizations={loadingOrganizations}
                    destinationTypes={destinationTypes}
                    loadingDestinationTypes={loadingDestinationTypes}
                    destinationUrls={destinationUrls}
                    loadingDestinationUrls={loadingDestinationUrls}
                    providerTypes={providerTypes}
                    loadingProviderTypes={loadingProviderTypes}
                    globalSearchStr={sourceFilter.search}
                    onChangeGlobalSearch={handleGlobalSearch}
                    onRefresh={() => setReloadSources(true)}
                    onChangeOwner={handleFilterOwner}
                    ownerFilterValue={ownerSelectValue}
                    onChangeEndpoint={handleFilterEndpoint}
                    endpointFilterValue={endpointSelectValue}
                    onChangeProviderType={handleFilterProviderType}
                    providerTypeFilterValue={providerTypeSelectValue}
                    onChangeDestinationType={handleFilterDestinationType}
                    destinationTypeFilterValue={destinationTypeSelectValue}
                    ordering={sourceFilter.ordering}
                    onChangeSortBy={handleSortBy}
                    onViewDetails={handleToggleDestinationDetailsModal}
                    onViewConnectionDetails={handleViewConnectionDetails}
                />
            </div>
        );
};

export default SourcesPage;
